import * as React from 'react'
import { focusRing } from '@repo/studio-design-system/utilities'
import { ChevronDownIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-down'
import { IconHolder } from '@repo/studio-components-icon-holder'
import { tw } from 'utils/classnames'

export interface WorkspaceTriggerProps extends Omit<React.ComponentProps<'button'>, 'ref'> {
  /** Flips the chevron and holds the open background while the menu is up. */
  open?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

/**
 * WorkspaceTrigger — fills the top bar's logo slot, showing the current
 * workspace's wordmark and opening the workspace menu.
 *
 * @figma https://www.figma.com/design/Jky2GUhAX9y6ajLFg921fb/AI-Marketing-Studio?node-id=3745-133219
 */
export function WorkspaceTrigger({
  open = false,
  type = 'button',
  className,
  children,
  ...props
}: WorkspaceTriggerProps) {
  return (
    <button
      type={type}
      {...props}
      className={tw(
        'inline-flex h-8 shrink-0 items-center gap-2 py-1 pl-2.5 pr-1.5',
        'rounded-studio-geometry-semantic-radius-sm outline-none transition-colors',
        focusRing,
        open ? triggerClasses.open : triggerClasses.closed,
        className,
      )}
    >
      <span className="flex items-center">{children}</span>
      <IconHolder
        size="medium"
        className={tw('shrink-0 transition-transform', open && 'rotate-180')}
      >
        <ChevronDownIcon />
      </IconHolder>
    </button>
  )
}

const triggerClasses = {
  closed: [
    'text-theme_semantic_action_select_tertiary_text-color-default',
    'hover:bg-theme_semantic_action_select_tertiary_background-color-hover',
    'active:bg-theme_semantic_action_select_tertiary_background-color-active',
    'focus-visible:bg-theme_semantic_action_select_tertiary_background-color-focus',
  ].join(' '),
  open: [
    'bg-theme_semantic_action_select_tertiary_background-color-open',
    'text-theme_semantic_action_select_tertiary_text-color-open',
  ].join(' '),
}
